import { Component, inject, OnInit, signal, HostListener, LOCALE_ID, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationType } from '../../shared/notification/notification.model';
import { ProductListItem, ProductFiltersApi } from '../../../pages/admin-dashboard/products/product.model';
import { ProductService } from '../../../services/product/product.service';
import { ProductsLookupService } from '../../../services/product/lookup.service';
import { LookupVTO } from '../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { finalize } from 'rxjs';
import { ViewProduct } from '../../../pages/admin-dashboard/products/admin-products.model';
import { CategoryService } from '../../../services/category/category.service';
import { SubcategoryResultSet } from '../../../pages/admin-dashboard/categories/category.model';
import { ProductsFileMgt } from '../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { AdminProductsListFilter } from './admin-products-list-filter/admin-products-list-filter';
import { Loading } from '../../../utils/loading/loading';
import { AdminCardView } from './admin-card-view/admin-card-view';
import { AdminListView } from './admin-list-view/admin-list-view';
import { ErrorData } from '../../../utils/error-data/error-data';
import { SuccessfulNotification } from '../../../utils/successful-notification/successful-notification';
import { ConfirmationDialog } from '../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { CurrentUserService } from '../../../services/current-user.service';
import { Pagination } from '../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
} from '../../../shared/ui/shared-tables/pagination/pagination.utils';

@Component({
  selector: 'app-admin-products-list',
  imports: [
    FormsModule,
    AdminProductsListFilter,
    Loading,
    AdminCardView,
    AdminListView,
    ErrorData,
    SuccessfulNotification,
    ConfirmationDialog,
    Pagination,
  ],
  templateUrl: './admin-products-list.html',
  styleUrl: './admin-products-list.scss',
})
export class AdminProductsList implements OnInit {
  private router = inject(Router);
  private productService = inject(ProductService);
  private lookupService = inject(ProductsLookupService);
  private categoryService = inject(CategoryService);
  private locale = inject(LOCALE_ID);
  private fileMgt = inject(ProductsFileMgt);
  private currentUserService = inject(CurrentUserService);

  unAuthorized = signal(false);

  viewMode = signal<'table' | 'cards'>('table');
  activeMenuId = signal<number | null>(null);
  totalItems = signal(0);
  currentPage = 1;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  showDeleteDialog = false;
  productToDelete = signal<ViewProduct | null>(null);

  notificationMessage = '';
  notificationType: NotificationType = 'success';
  showNotificationFlag = false;

  categories = signal<LookupVTO[]>([]);
  mfgCompanies = signal<LookupVTO[]>([]);
  subCategories = signal<SubcategoryResultSet | undefined>(undefined);
  productImages = new Map<string, { url: SafeUrl; isImage: boolean }>();
  pageSize = signal<number>(25);

  filters: ProductFiltersApi = {
    hasSubProducts: '',
    productTitle: '',
    mfgCompanyId: undefined,
    categoryId: undefined,
    subCategoryId: undefined,
    customerPriceFrom: 0,
    customerPriceTo: 0,
    businessPriceFrom: 0,
    businessPriceTo: 0,
    expireDateFrom: undefined,
    expireDateTo: undefined,
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
    createdOnFrom: '',
    createdOnTo: '',
    lastModifiedOnFrom: '',
    lastModifiedOnTo: '',
    status: 'active',
  };

  appliedFilters: ProductFiltersApi = { ...this.filters };
  hasSubproducts = signal<boolean | ''>('');

  products = signal<ViewProduct[]>([]);
  allProducts = signal<ViewProduct[]>([]);

  loading = signal<boolean>(false);
  errorGettingProducts = signal<string | null>(null);

  productTitleResults: { id: number; titleAr: string; titleEn: string }[] = [];
  productTitleLoading = false;
  private productTitleSearchTimeout: any;
  showProductTitleDropdown = false;

  activeDropdownId = signal<number | null>(null);

  ngOnInit() {
    this.loadLookups();
    this.loadProducts();
  }

  isFilterApplied(): boolean {
    const f = this.appliedFilters;
    return !!(
      f.productTitle ||
      f.mfgCompanyId ||
      f.categoryId ||
      f.subCategoryId ||
      f.customerPriceFrom ||
      f.customerPriceTo ||
      f.businessPriceFrom ||
      f.businessPriceTo ||
      f.createdOnFrom ||
      f.createdOnTo ||
      f.lastModifiedOnFrom ||
      f.lastModifiedOnTo ||
      f.hasSubProducts
    );
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage = 1;
    this.loadProducts();
  }

  onCategoryChange(categoryId: number | undefined) {
    this.filters.subCategoryId = undefined;
    this.subCategories.set(undefined);
    if (categoryId) {
      this.categoryService.getSubcategories({ categoryId }).subscribe({
        next: (res) => this.subCategories.set(res),
        error: (err) => {
          if (err.status === 403) {
            this.unAuthorized.set(true);
            return;
          }
          console.error('Error loading subcategories');
        },
      });
    }
  }

  onProductTitleChange(value: string) {
    this.filters.productTitle = value;
    clearTimeout(this.productTitleSearchTimeout);
    if (!value) {
      this.productTitleResults = [];
      this.productTitleLoading = false;
      this.showProductTitleDropdown = false;
      return;
    }
    this.showProductTitleDropdown = true;
    this.productTitleLoading = true;
    this.productTitleSearchTimeout = setTimeout(() => {
      this.searchProductTitles(value);
    }, 400);
  }

  onProductTitleFocus() {
    if (this.filters.productTitle) {
      this.showProductTitleDropdown = true;
      if (!this.productTitleResults.length) {
        this.productTitleLoading = true;
        this.searchProductTitles(this.filters.productTitle);
      }
    }
  }

  selectProductTitle(item: { id: number; titleAr: string; titleEn: string }) {
    this.filters.productTitle = this.isArabic() ? item.titleAr : item.titleEn;
    this.showProductTitleDropdown = false;
  }

  private searchProductTitles(term: string) {
    this.productService.getAllAdminProducts({ productTitle: term }, this.isArabic() ? 'ar' : 'en').subscribe({
      next: (res) => {
        this.productTitleResults = res.list?.map((p) => ({ id: p.id, titleAr: p.titleAr!, titleEn: p.titleEn })) ?? [];
        this.productTitleLoading = false;
      },
      error: () => {
        this.productTitleResults = [];
        this.productTitleLoading = false;
      },
    });
  }

  loadLookups() {
    this.lookupService.getLookupCategories().subscribe((res) => this.categories.set(res.list));
    this.lookupService.getLookupMFGCompanies().subscribe((res) => this.mfgCompanies.set(res.list));
  }

  loadProducts() {
    this.loading.set(true);
    this.errorGettingProducts.set(null);

    const apiFilters: ProductFiltersApi = {
      hasSubProducts: this.appliedFilters.hasSubProducts,
      productTitle: this.appliedFilters.productTitle || undefined,
      mfgCompanyId: this.appliedFilters.mfgCompanyId,
      categoryId: this.appliedFilters.categoryId,
      subCategoryId: this.appliedFilters.subCategoryId,
      customerPriceFrom: this.appliedFilters.customerPriceFrom,
      customerPriceTo: this.appliedFilters.customerPriceTo,
      businessPriceFrom: this.appliedFilters.businessPriceFrom,
      businessPriceTo: this.appliedFilters.businessPriceTo,
      pageNum: this.currentPage - 1,
      pageSize: this.pageSize(),
      orderBy: this.appliedFilters.orderBy as any,
      orderDir: this.appliedFilters.orderDir as 'ASC' | 'DESC',
      createdOnFrom: this.appliedFilters.createdOnFrom,
      createdOnTo: this.appliedFilters.createdOnTo,
      lastModifiedOnFrom: this.appliedFilters.lastModifiedOnFrom,
      lastModifiedOnTo: this.appliedFilters.lastModifiedOnTo,
      status: this.appliedFilters.status,
    };

    this.productService
      .getAllAdminProducts(apiFilters, this.isArabic() ? 'ar' : 'en')
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: any) => {
          this.totalItems.set(res.total);
          this.products.set(res.list);
          this.allProducts.set(res.list);

          res.list.forEach((product: ViewProduct) => {
            if (product.profileImageFid) {
              this.fileMgt.getImageUrl(product.profileImageFid).subscribe({
                next: (data) => {
                  this.productImages.set(product.id.toString(), data);
                },
                error: () => {
                  this.productImages.set(product.id.toString(), { url: '', isImage: false });
                },
              });
            }
          });
        },
        error: (error) => {
          this.errorGettingProducts.set(
            this.isArabic() ? error.messageAr || 'حدث بعض الخطأ!' : error.messageEn || 'some error occured!',
          );
        },
      });
  }

  applyFilters() {
    this.appliedFilters = { ...this.filters };
    this.currentPage = 1;
    this.loadProducts();
  }

  onHasSubProductsChange(value: boolean | '') {
    this.appliedFilters = { ...this.filters, hasSubProducts: value };
    this.hasSubproducts.set(value);
    this.loadProducts();
  }

  resetFilters() {
    this.filters = {
      hasSubProducts: '',
      productTitle: '',
      mfgCompanyId: undefined,
      categoryId: undefined,
      subCategoryId: undefined,
      customerPriceFrom: 0,
      customerPriceTo: 0,
      businessPriceFrom: 0,
      businessPriceTo: 0,
      expireDateFrom: undefined,
      expireDateTo: undefined,
    };
    this.subCategories.set(undefined);
    this.applyFilters();
  }

  private showNotification(message: string, type: NotificationType) {
    this.notificationMessage = message;
    this.notificationType = type;
    this.showNotificationFlag = true;
    setTimeout(() => (this.showNotificationFlag = false), 3000);
  }

  sort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
  }

  get sortedProducts(): ViewProduct[] {
    if (!this.sortColumn) return this.products();
    return [...this.products()].sort((a, b) => {
      const aValue = (a as any)[this.sortColumn];
      const bValue = (b as any)[this.sortColumn];
      if (aValue == null || bValue == null) return 0;
      return this.sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : aValue > bValue ? -1 : 1;
    });
  }

  get paginatedProducts() {
    return this.products;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalItems() / this.pageSize()));
  }

  get startItem() {
    return getStartItem(this.currentPage, this.pageSize(), this.totalItems());
  }

  get endItem() {
    return getEndItem(this.currentPage, this.pageSize(), this.totalItems());
  }

  get pageNumbers() {
    return buildPageNumbers(this.currentPage, this.totalPages);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadProducts();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadProducts();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadProducts();
    }
  }

  openDeleteDialog(item: { product: ViewProduct }) {
    this.productToDelete.set(item.product);
    this.showDeleteDialog = true;
  }

  closeDeleteDialog() {
    this.showDeleteDialog = false;
    this.productToDelete.set(null);
  }

  onDeleteSuccess(item: { product: ViewProduct }) {
    this.loadProducts();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.activeDropdownId.set(null);
    this.showProductTitleDropdown = false;
  }

  toggleDropdown(item: { id: number; event: Event }) {
    item.event.stopPropagation();
    this.activeDropdownId.set(this.activeDropdownId() === item.id ? null : item.id);
  }

  onViewProduct(item: { productID: number }) {
    this.router.navigate(['/admin/products/view-product', item.productID]);
  }

  onEditProduct(item: { productID: number }) {
    this.router.navigate(['/admin/products/edit-product', item.productID]);
  }

  toggleSortOrder() {
    this.filters.orderDir = this.filters.orderDir === 'ASC' ? 'DESC' : 'ASC';
    this.appliedFilters = { ...this.filters };
    this.loadProducts();
  }

  confirmDelete() {
    if (!this.productToDelete()) return;
    this.loading.set(true);
    this.productService
      .deleteProduct(this.productToDelete()!.id)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.loadProducts();
          this.showNotification('Product deleted successfully', 'success');
        },
        error: (err) => {
          console.error('Delete failed', err);
          this.showNotification('Failed to delete product', 'error');
        },
      });
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  isEnglish() {
    return this.locale.startsWith('en');
  }

  redirectToLogin() {
    this.router.navigate(['/login']);
    this.currentUserService.logout();
  }
}
